import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  formatDate,
  startOfDayInTZ,
} from '#common/utils';

/**
 * Production Schedule (full-width band).
 *
 * Shows all color variants simultaneously, each with its own table section.
 * Rows are arrival lots (In Stock, Apr 20 …) and columns are sizes.
 * Each cell renders the available qty + a small circular "+" add button.
 *
 * Props:
 *   - product: { inventoryProduct, colors, sizes, ... }
 *   - readOnly: when true we hide the add buttons (used by inventory orders)
 *   - onSelect({ color, size, maxAmounts, inventoryItem })
 */

interface InventoryItem {
  color?: string;
  size?: string;
  amounts: number;
  availabilityStatus?: string;
  availableDate?: string | Date | null;
  [key: string]: unknown;
}

interface AvailabilityGroup {
  availableDate: Date;
  displayStatus: string;
  originalStatus?: string | undefined;
  items: InventoryItem[];
}

interface ColorGroup {
  color?: string | undefined;
  items: AvailabilityGroup[];
}

interface InventoryProductData {
  inventoryProduct?: InventoryItem[] | undefined;
  colors?: string[];
  sizes?: string[];
  [key: string]: unknown;
}

interface SelectPayload {
  color?: string | undefined;
  size?: string | undefined;
  maxAmounts: number;
  inventoryItem: InventoryItem;
}

interface InventoryProductProps {
  product: InventoryProductData;
  onSelect: (payload: SelectPayload) => void;
  readOnly?: boolean;
  // Market timezone used for day grouping/labels so the storefront agrees with
  // the server-side stock range (see add-to-cart). Omit for viewer-local.
  timeZone?: string | undefined;
}

interface StatusLabel {
  title: ReactNode;
  sub: string;
  pill?: string;
}

function statusLabel(
  displayStatus: string,
  availableDate: Date,
  t: TFunction,
  timeZone?: string,
): StatusLabel {
  switch (displayStatus) {
    case 'inStock':
      return { title: t('inventory.inStock'), sub: t('inventory.shipsWithin5Days') };
    case 'pending':
      return { title: t('inventory.pending'), sub: t('inventory.awaitingConfirmation') };
    default:
      return {
        title: formatDate(availableDate, '', { timeZone }),
        pill: '',
        sub: t('inventory.estimatedDelivery'),
      };
  }
}

export default function InventoryProduct(props: InventoryProductProps) {
  const {
    product,
    onSelect,
    readOnly,
    timeZone,
  } = props;
  const { t } = useTranslation('shop');

  const { inventoryProduct } = product;

  if (!inventoryProduct || !inventoryProduct.length) {
    return null;
  }

  // ---- Group inventory by color (preserves the original grouping logic) ---
  /*
    Final structure per color:
    {
      color: 'Ivory',
      items: [{
        availableDate: Date,
        displayStatus: 'inStock' | 'pending' | 'inProduction',
        originalStatus: '...',
        items: [{ amounts, color, size, ... }, ...]
      }, ...]
    }
  */
  const inventoryData = inventoryProduct.reduce<ColorGroup[]>((data, ip) => {
    const displayStatus = ip.availabilityStatus === 'pastDue' ? 'pending' : ip.availabilityStatus;

    let colorGroup = data.find((d) => d.color === ip.color);
    if (!colorGroup) {
      colorGroup = { color: ip.color, items: [] };
      data.push(colorGroup);
    }

    let availabilityGroup = colorGroup.items.find((cg) => {
      if (displayStatus === 'inStock') {
        return cg.displayStatus === 'inStock';
      }
      if (displayStatus === 'pending' && cg.displayStatus === 'pending') {
        return true;
      }
      const isSameDate = (t1?: string | Date | null, t2?: string | Date | null) => {
        if (!t1 || !t2) return false;
        // Compare on the MARKET-timezone calendar day so this grouping matches
        // the server-side stock range (startOfDayInTZ/endOfDayInTZ).
        return startOfDayInTZ(timeZone, t1).getTime() === startOfDayInTZ(timeZone, t2).getTime();
      };
      return (cg.displayStatus === displayStatus
        && (!!cg.availableDate && !!ip.availableDate
          && isSameDate(cg.availableDate, ip.availableDate)));
    });

    if (!availabilityGroup) {
      availabilityGroup = {
        availableDate: displayStatus === 'inStock'
          ? new Date('1970-01-01')
          : new Date(ip.availableDate as string | Date),
        displayStatus: displayStatus as string,
        originalStatus: ip.availabilityStatus,
        items: [],
      };
      colorGroup.items.push(availabilityGroup);
    }

    availabilityGroup.items.push(ip);
    return data;
  }, []);

  // sort grouped data by color, mirroring the order from the product itself
  const productColors = product.colors || [];
  inventoryData.sort((a, b) => (
    productColors.indexOf(a.color as string) - productColors.indexOf(b.color as string)
  ));
  inventoryData.forEach((data) => {
    data.items.sort((a, b) => {
      const statusPriority: Record<string, number> = { inStock: 0, pending: 1, inProduction: 2 };
      const statusDiff = statusPriority[a.displayStatus]! - statusPriority[b.displayStatus]!;
      return statusDiff || (a.availableDate.getTime() - b.availableDate.getTime());
    });
  });

  // Sizes shown as columns. Pull from the product to keep ordering consistent
  // with the size chip rail above.
  const sizes = product.sizes || [];

  const renderCell = (row: AvailabilityGroup, size: string) => {
    const matched = row.items.filter((itm) => itm.size === size);
    const amounts = matched.reduce((acc, itm) => acc + itm.amounts, 0);
    const sample = matched[0];
    const hasStock = amounts > 0;
    const isSelectable = row.displayStatus !== 'pending' && hasStock;

    return (
      <td key={`${row.displayStatus}-${size}`}>
        <span className={`inv-cell${hasStock && row.displayStatus !== 'pending' ? ' has-stock' : ''}`}>
          <span className={`qty-num${hasStock ? '' : ' zero'}`}>{amounts}</span>
          {!readOnly ? (
            <button
              type="button"
              className="add-btn"
              disabled={!isSelectable || !sample}
              title={isSelectable ? t('inventory.addAria', { size }) : ''}
              onClick={() => {
                if (!isSelectable || !sample) return;
                onSelect({
                  color: sample.color,
                  size: sample.size,
                  maxAmounts: amounts,
                  inventoryItem: {
                    ...sample,
                    amounts,
                  },
                });
              }}
            >
              <AddIcon />
            </button>
          ) : null}
        </span>
      </td>
    );
  };

  const renderColorTable = (group: ColorGroup) => (
    <div key={group.color} className="inv-color-section">
      <div className="inv-color-label">{group.color}</div>
      <div className="inv-scroll">
        <table className="inv-tbl">
          <thead>
            <tr>
              <th className="col-arrival">{t('inventory.expectedArrival')}</th>
              {sizes.map((s) => (
                <th key={`size-${s}`}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {group.items.map((row) => {
              const { title, pill, sub } = statusLabel(row.displayStatus, row.availableDate, t, timeZone);
              return (
                <tr
                  key={`${row.displayStatus}-${String(row.availableDate)}`}
                  className={row.displayStatus === 'pending' ? 'pending' : ''}
                >
                  <td className="col-arrival">
                    <div className="arr-title">
                      {title}
                      {pill ? <span className="pill">{pill}</span> : null}
                    </div>
                    <div className="arr-sub">{sub}</div>
                  </td>
                  {sizes.map((s) => renderCell(row, s))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <Box className="pd-inv">
      <div className="inv-head">
        <div>
          <div className="inv-title">{t('inventory.title')}</div>
          <div className="inv-sub">{t('inventory.subtitle')}</div>
        </div>
        <div className="inv-legend">
          <span className="lg">
            <span className="dot" style={{ background: 'var(--pd-brand-navy)' }} />
            {t('inventory.legendAvailable')}
          </span>
          <span className="lg">
            <span
              className="dot"
              style={{ background: 'var(--pd-bg-paper)', border: '1px solid var(--pd-divider-strong)' }}
            />
            {t('inventory.legendBackorder')}
          </span>
        </div>
      </div>

      {inventoryData.map(renderColorTable)}

      <div
        className="tf-hint"
        style={{ fontStyle: 'italic', marginTop: 10 }}
      >
        {t('inventory.singlePieceNote')}
      </div>
    </Box>
  );
}
